import React, { useEffect, useState } from "react";
import { Container, Card, Table, Spinner, Alert, Button, Badge, Form } from "react-bootstrap";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useAuth } from "../../contexts/AuthContext";
import { fires } from "../../firebase";
import { useNavigate } from "react-router-dom";

const normalizeDescription = (value) => {
  if (!value) return "";
  if (typeof value === "string") {
    return value
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }
  return String(value).replace(/\s+/g, " ").trim();
};

const getEventDescription = (data) => {
  const localizedDescription =
    data.descriptions?.en ||
    data.descriptions?.english ||
    data.descriptions?.zh ||
    data.descriptions?.chinese;
  return normalizeDescription(data.description || localizedDescription || data.subtitle || data.shortDescription || "");
};

export default function AdminEventOverview() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [checkingAdmin, setCheckingAdmin] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState("");
  const [events, setEvents] = useState([]);
  const [selectedMonthKey, setSelectedMonthKey] = useState("all");
  const [summarizingId, setSummarizingId] = useState(null);
  const [summarizingAll, setSummarizingAll] = useState(false);
  const [summarizeProgress, setSummarizeProgress] = useState({ current: 0, total: 0 });
  const [updatingApprovalId, setUpdatingApprovalId] = useState(null);
  const [loadingCustomerPreferences, setLoadingCustomerPreferences] = useState(false);
  const [customerPreferenceRows, setCustomerPreferenceRows] = useState([]);
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    checkAdmin();
  }, [currentUser]);

  useEffect(() => {
    if (isAdmin) {
      loadEvents();
    }
  }, [isAdmin]);

  const checkAdmin = async () => {
    try {
      if (!currentUser) {
        navigate("/");
        return;
      }
      const adminDoc = await fires.collection("admin").doc(currentUser.uid).get();
      if (!adminDoc.exists) {
        setIsAdmin(false);
        setCheckingAdmin(false);
        return;
      }
      const roles = adminDoc.data()?.roles || [];
      if (!roles.includes("management")) {
        setIsAdmin(false);
        setCheckingAdmin(false);
        return;
      }
      setIsAdmin(true);
      setCheckingAdmin(false);
    } catch (err) {
      console.error("Admin check failed:", err);
      setError("Failed to verify admin access");
      setIsAdmin(false);
      setCheckingAdmin(false);
    }
  };

  const loadEvents = async () => {
    setLoading(true);
    setError("");
    try {
      let snapshot;
      try {
        snapshot = await fires
          .collection("events")
          .orderBy("startDate", "asc")
          .get();
      } catch (e1) {
        // Fallback without index: get all and sort in memory
        if (e1.code === "failed-precondition" || e1.code === "unavailable") {
          snapshot = await fires.collection("events").get();
        } else {
          throw e1;
        }
      }

      const list = snapshot.docs.map((doc) => {
        const data = doc.data() || {};
        const rawStart = data.startDate?.toDate?.() ?? data.startDate;
        const rawEnd = data.endDate?.toDate?.() ?? data.endDate;
        const startDate =
          rawStart instanceof Date ? rawStart : rawStart ? new Date(rawStart) : null;
        const endDate =
          rawEnd instanceof Date ? rawEnd : rawEnd ? new Date(rawEnd) : null;

        const summary = data.summary || {};
        const memberCount =
          typeof summary.users === "number"
            ? summary.users
            : summary.users != null
            ? Number(summary.users) || 0
            : 0;
        const ticketCount =
          typeof summary.tickets === "number"
            ? summary.tickets
            : summary.tickets != null
            ? Number(summary.tickets) || 0
            : 0;
        const viewCount =
          typeof summary.views === "number"
            ? summary.views
            : summary.views != null
            ? Number(summary.views) || 0
            : 0;
        const orderCount =
          typeof summary.orders === "number"
            ? summary.orders
            : summary.orders != null
            ? Number(summary.orders) || 0
            : 0;
        const rawOrderTotal =
          typeof summary.orderTotal === "number"
            ? summary.orderTotal
            : summary.orderTotal != null
            ? Number(summary.orderTotal) || 0
            : 0;
        const orderTotal = Math.round(rawOrderTotal * 100) / 100;
        return {
          id: doc.id,
          title: data.title || data.eventTitle || "Untitled event",
          description: getEventDescription(data),
          orgId: data.orgId || "",
          status: data.status || "draft",
          type: data.type || "",
          approved: data.approved,
          startDate,
          endDate,
          summary,
          memberCount,
          ticketCount,
          viewCount,
          orderCount,
          orderTotal,
        };
      });

      // If we couldn't order in Firestore, sort here
      if (!snapshot.query?.orderBy) {
        list.sort((a, b) => {
          const ta = a.startDate ? a.startDate.getTime() : Infinity;
          const tb = b.startDate ? b.startDate.getTime() : Infinity;
          return ta - tb;
        });
      }

      setEvents(list);
    } catch (err) {
      console.error("Error loading events:", err);
      setError(err?.message || "Failed to load events");
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (date) => {
    if (!date) return "—";
    const d = date instanceof Date ? date : new Date(date);
    return isNaN(d.getTime()) ? "—" : d.toLocaleString();
  };

  const formatMonthLabel = (date) => {
    if (!date) return "—";
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return "—";
    return `${d.toLocaleString(undefined, { month: "short" })}-${String(d.getFullYear()).slice(-2)}`;
  };

  const getEventOrderMetrics = (event) => {
    const summary = event.summary || {};
    const orders =
      (typeof summary.orders === "number" ? summary.orders : 0) ||
      (typeof event.orderCount === "number" ? event.orderCount : 0);
    const rawOrderTotal =
      (typeof summary.orderTotal === "number" ? summary.orderTotal : 0) ||
      (typeof event.orderTotal === "number" ? event.orderTotal : 0);
    const orderTotal = Math.round(rawOrderTotal * 100) / 100;

    let freeOrders = 0;
    let paidOrders = 0;
    if (typeof summary.freeOrders === "number" || typeof summary.paidOrders === "number") {
      freeOrders = typeof summary.freeOrders === "number" ? summary.freeOrders : 0;
      paidOrders = typeof summary.paidOrders === "number" ? summary.paidOrders : 0;
    } else if (orders > 0) {
      if (orderTotal === 0) {
        freeOrders = orders;
      } else {
        paidOrders = orders;
      }
    }

    return {
      ...event,
      orders,
      paidOrders,
      freeOrders,
      orderTotal,
      monthLabel: formatMonthLabel(event.startDate),
    };
  };

  const getEventViewMetrics = (event) => {
    const summary = event.summary || {};
    const views =
      (typeof summary.views === "number" ? summary.views : 0) ||
      (typeof event.viewCount === "number" ? event.viewCount : 0);
    const tickets =
      (typeof summary.tickets === "number" ? summary.tickets : 0) ||
      (typeof event.ticketCount === "number" ? event.ticketCount : 0);
    const orders =
      (typeof summary.orders === "number" ? summary.orders : 0) ||
      (typeof event.orderCount === "number" ? event.orderCount : 0);

    return {
      ...event,
      views,
      tickets,
      orders,
      type: event.type || "Uncategorized",
      monthLabel: formatMonthLabel(event.startDate),
    };
  };

  const getMostCommonValue = (rows, field) => {
    const counts = new Map();
    rows.forEach((row) => {
      const value = row[field] || "";
      if (!value) return;
      counts.set(value, (counts.get(value) || 0) + 1);
    });

    let best = null;
    counts.forEach((count, value) => {
      if (!best || count > best.count) {
        best = { value, count };
      }
    });
    return best;
  };

  const orderReport = (() => {
    const rows = events.map(getEventOrderMetrics);
    const byOrders = rows
      .filter((event) => event.orders > 0)
      .sort((a, b) => b.orders - a.orders || b.paidOrders - a.paidOrders || b.orderTotal - a.orderTotal)
      .slice(0, 150);
    const byOrderAmount = rows
      .filter((event) => event.orderTotal > 0)
      .sort((a, b) => b.orderTotal - a.orderTotal || b.orders - a.orders || b.paidOrders - a.paidOrders)
      .slice(0, 150);

    const orderTopIds = new Set(byOrders.map((event) => event.id));
    const overlap = byOrderAmount.filter((event) => orderTopIds.has(event.id));
    const leaders = [...byOrders, ...byOrderAmount].filter(
      (event, index, arr) => arr.findIndex((item) => item.id === event.id) === index
    );
    const statusCommon = getMostCommonValue(leaders, "status");
    const typeCommon = getMostCommonValue(leaders, "type");
    const orgCommon = getMostCommonValue(leaders, "orgId");
    const monthCommon = getMostCommonValue(leaders, "monthLabel");

    const similarityNotes = [];
    if (byOrders[0] && byOrderAmount[0]) {
      if (byOrders[0].id === byOrderAmount[0].id) {
        similarityNotes.push(`${byOrders[0].title} leads both total orders and order amount.`);
      } else if (overlap.length > 0) {
        similarityNotes.push(`${overlap.length} event${overlap.length === 1 ? "" : "s"} appear in both top lists.`);
      } else {
        similarityNotes.push("The top ordered events and top order-amount events are different.");
      }
    }
    if (statusCommon && statusCommon.count > 1) {
      similarityNotes.push(`${statusCommon.count} leading events share status: ${statusCommon.value}.`);
    }
    if (typeCommon && typeCommon.count > 1) {
      similarityNotes.push(`${typeCommon.count} leading events share type: ${typeCommon.value}.`);
    }
    if (orgCommon && orgCommon.count > 1) {
      similarityNotes.push(`${orgCommon.count} leading events share org: ${orgCommon.value}.`);
    }
    if (monthCommon && monthCommon.count > 1 && monthCommon.value !== "—") {
      similarityNotes.push(`${monthCommon.count} leading events started in ${monthCommon.value}.`);
    }
    if (similarityNotes.length === 0) {
      similarityNotes.push("No strong shared pattern is visible among the current leaders.");
    }

    return {
      byOrders,
      byOrderAmount,
      overlap,
      similarityNotes,
    };
  })();

  const currentYearOrderReport = (() => {
    const rows = events
      .map(getEventOrderMetrics)
      .filter((event) => event.startDate && event.startDate.getFullYear() === currentYear);
    const byOrders = rows
      .filter((event) => event.orders > 0)
      .sort((a, b) => b.orders - a.orders || b.paidOrders - a.paidOrders || b.orderTotal - a.orderTotal)
      .slice(0, 150);
    const byOrderAmount = rows
      .filter((event) => event.orderTotal > 0)
      .sort((a, b) => b.orderTotal - a.orderTotal || b.orders - a.orders || b.paidOrders - a.paidOrders)
      .slice(0, 150);

    return {
      byOrders,
      byOrderAmount,
    };
  })();

  const popularEventReport = (() => {
    const rows = events.map(getEventViewMetrics);
    const byViews = rows
      .filter((event) => event.views > 0)
      .sort((a, b) => b.views - a.views || b.orders - a.orders || b.tickets - a.tickets)
      .slice(0, 150);

    return {
      byViews,
    };
  })();

  const currentYearPopularEventReport = (() => {
    const rows = events
      .map(getEventViewMetrics)
      .filter((event) => event.startDate && event.startDate.getFullYear() === currentYear);
    const byViews = rows
      .filter((event) => event.views > 0)
      .sort((a, b) => b.views - a.views || b.orders - a.orders || b.tickets - a.tickets)
      .slice(0, 150);

    return {
      byViews,
    };
  })();

  const downloadCsv = (filename, header, rows) => {
    const csvLines = [header, ...rows]
      .map((cols) =>
        cols
          .map((val) => {
            const s = String(val ?? "");
            if (s.includes(",") || s.includes('"') || s.includes("\n")) {
              return `"${s.replace(/"/g, '""')}"`;
            }
            return s;
          })
          .join(",")
      )
      .join("\n");
    const blob = new Blob([csvLines], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getSortedCounts = (map, limit = 5) => {
    return Array.from(map.entries())
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .slice(0, limit)
      .map(([label, count]) => ({ label, count }));
  };

  const formatCountTags = (items) => {
    return items.map((item) => `${item.label} (${item.count})`).join("; ");
  };

  const getOrderDate = (orderData) => {
    const rawDate =
      orderData.timestamp?.toDate?.() ||
      orderData.createdAt?.toDate?.() ||
      orderData.updatedAt?.toDate?.() ||
      orderData.date ||
      null;
    if (!rawDate) return null;
    const date = rawDate instanceof Date ? rawDate : new Date(rawDate);
    return isNaN(date.getTime()) ? null : date;
  };

  const getSuggestedCustomerTags = (row) => {
    const tags = row.topTypes.map((item) => item.label);
    if (row.orderCount >= 5) tags.push("frequent-customer");
    if (row.totalSpent >= 100) tags.push("high-value-customer");
    if (row.paidOrderCount > 0 && row.freeOrderCount === 0) tags.push("paid-only");
    if (row.freeOrderCount > 0 && row.paidOrderCount === 0) tags.push("free-only");
    if (row.paidOrderCount > 0 && row.freeOrderCount > 0) tags.push("mixed-free-paid");
    return Array.from(new Set(tags)).join("; ");
  };

  const loadCustomerPreferences = async () => {
    setLoadingCustomerPreferences(true);
    setError("");
    try {
      const eventById = new Map(events.map((event) => [event.id, event]));
      const snapshot = await fires.collection("orders").get();
      const customerMap = new Map();

      snapshot.docs.forEach((doc) => {
        const orderData = doc.data() || {};
        const email = String(orderData.userEmail || "").trim().toLowerCase();
        if (!email) return;

        const event = eventById.get(orderData.eventId);
        const eventTitle = event?.title || orderData.eventTitle || orderData.title || orderData.eventId || "Unknown event";
        const eventType = event?.type || "Uncategorized";
        const orgId = event?.orgId || orderData.orgId || "";
        const amount = Number(orderData.amount) || 0;
        const orderDate = getOrderDate(orderData);

        const existing =
          customerMap.get(email) || {
            email,
            names: new Set(),
            userIds: new Set(),
            orderCount: 0,
            paidOrderCount: 0,
            freeOrderCount: 0,
            totalSpent: 0,
            eventCounts: new Map(),
            typeCounts: new Map(),
            orgCounts: new Map(),
            lastOrderDate: null,
          };

        if (orderData.userName) existing.names.add(orderData.userName);
        if (orderData.userUID) existing.userIds.add(orderData.userUID);
        existing.orderCount += 1;
        existing.totalSpent = Math.round((existing.totalSpent + amount) * 100) / 100;
        if (amount > 0) {
          existing.paidOrderCount += 1;
        } else {
          existing.freeOrderCount += 1;
        }
        existing.eventCounts.set(eventTitle, (existing.eventCounts.get(eventTitle) || 0) + 1);
        existing.typeCounts.set(eventType, (existing.typeCounts.get(eventType) || 0) + 1);
        if (orgId) existing.orgCounts.set(orgId, (existing.orgCounts.get(orgId) || 0) + 1);
        if (orderDate && (!existing.lastOrderDate || orderDate > existing.lastOrderDate)) {
          existing.lastOrderDate = orderDate;
        }

        customerMap.set(email, existing);
      });

      const rows = Array.from(customerMap.values())
        .map((customer) => {
          const topTypes = getSortedCounts(customer.typeCounts, 5);
          const topEvents = getSortedCounts(customer.eventCounts, 5);
          const topOrgs = getSortedCounts(customer.orgCounts, 5);
          const row = {
            email: customer.email,
            names: Array.from(customer.names).join("; "),
            userIds: Array.from(customer.userIds).join("; "),
            orderCount: customer.orderCount,
            paidOrderCount: customer.paidOrderCount,
            freeOrderCount: customer.freeOrderCount,
            totalSpent: customer.totalSpent,
            topTypes,
            topEvents,
            topOrgs,
            lastOrderDate: customer.lastOrderDate,
          };
          return {
            ...row,
            suggestedTags: getSuggestedCustomerTags(row),
          };
        })
        .sort((a, b) => b.orderCount - a.orderCount || b.totalSpent - a.totalSpent || a.email.localeCompare(b.email));

      setCustomerPreferenceRows(rows);
    } catch (err) {
      console.error("Error loading customer preferences:", err);
      setError(err?.message || "Failed to load customer preferences");
      setCustomerPreferenceRows([]);
    } finally {
      setLoadingCustomerPreferences(false);
    }
  };

  const handleExportCustomerPreferences = () => {
    if (!customerPreferenceRows.length) return;
    downloadCsv(
      "all-time-customer-event-preferences.csv",
      [
        "Email",
        "Names",
        "User IDs",
        "Order count",
        "Paid orders",
        "Free orders",
        "Total spent",
        "Favorite event types",
        "Top events",
        "Top orgs",
        "Suggested tags",
        "Last order date",
      ],
      customerPreferenceRows.map((row) => [
        row.email,
        row.names,
        row.userIds,
        row.orderCount,
        row.paidOrderCount,
        row.freeOrderCount,
        row.totalSpent.toFixed(2),
        formatCountTags(row.topTypes),
        formatCountTags(row.topEvents),
        formatCountTags(row.topOrgs),
        row.suggestedTags,
        row.lastOrderDate ? formatDateTime(row.lastOrderDate) : "",
      ])
    );
  };

  const handleExportPopularEvents = () => {
    if (!popularEventReport.byViews.length) return;
    downloadCsv(
      "top-150-most-viewed-events.csv",
      ["Rank", "Event", "Description", "Event ID", "Type", "Status", "Org ID", "Start month", "Views", "Tickets", "Orders"],
      popularEventReport.byViews.map((event, index) => [
        index + 1,
        event.title,
        event.description || "",
        event.id,
        event.type,
        event.status || "",
        event.orgId || "",
        event.monthLabel,
        event.views,
        event.tickets,
        event.orders,
      ])
    );
  };

  const handleExportCurrentYearPopularEvents = () => {
    if (!currentYearPopularEventReport.byViews.length) return;
    downloadCsv(
      `${currentYear}-top-150-most-viewed-events.csv`,
      ["Rank", "Event", "Description", "Event ID", "Type", "Status", "Org ID", "Start month", "Views", "Tickets", "Orders"],
      currentYearPopularEventReport.byViews.map((event, index) => [
        index + 1,
        event.title,
        event.description || "",
        event.id,
        event.type,
        event.status || "",
        event.orgId || "",
        event.monthLabel,
        event.views,
        event.tickets,
        event.orders,
      ])
    );
  };

  const getOrderEventCsvRows = (rows) => {
    return rows.map((event, index) => [
      index + 1,
      event.title,
      event.description || "",
      event.id,
      event.type || "",
      event.status || "",
      event.orgId || "",
      event.monthLabel,
      event.orders,
      event.paidOrders,
      event.freeOrders,
      event.orderTotal.toFixed(2),
    ]);
  };

  const handleExportMostOrderedEvents = () => {
    if (!orderReport.byOrders.length) return;
    downloadCsv(
      "top-150-most-ordered-events.csv",
      ["Rank", "Event", "Description", "Event ID", "Type", "Status", "Org ID", "Start month", "Orders", "Paid orders", "Free orders", "Order amount"],
      getOrderEventCsvRows(orderReport.byOrders)
    );
  };

  const handleExportOrderAmountEvents = () => {
    if (!orderReport.byOrderAmount.length) return;
    downloadCsv(
      "top-150-order-amount-events.csv",
      ["Rank", "Event", "Description", "Event ID", "Type", "Status", "Org ID", "Start month", "Orders", "Paid orders", "Free orders", "Order amount"],
      getOrderEventCsvRows(orderReport.byOrderAmount)
    );
  };

  const handleExportCurrentYearMostOrderedEvents = () => {
    if (!currentYearOrderReport.byOrders.length) return;
    downloadCsv(
      `${currentYear}-top-150-most-ordered-events.csv`,
      ["Rank", "Event", "Description", "Event ID", "Type", "Status", "Org ID", "Start month", "Orders", "Paid orders", "Free orders", "Order amount"],
      getOrderEventCsvRows(currentYearOrderReport.byOrders)
    );
  };

  const handleExportCurrentYearOrderAmountEvents = () => {
    if (!currentYearOrderReport.byOrderAmount.length) return;
    downloadCsv(
      `${currentYear}-top-150-order-amount-events.csv`,
      ["Rank", "Event", "Description", "Event ID", "Type", "Status", "Org ID", "Start month", "Orders", "Paid orders", "Free orders", "Order amount"],
      getOrderEventCsvRows(currentYearOrderReport.byOrderAmount)
    );
  };

  const renderViewedEventsTable = (rows, emptyText) => {
    if (rows.length === 0) {
      return <div className="text-muted small">{emptyText}</div>;
    }

    return (
      <div className="table-responsive" style={{ maxHeight: 520, overflowY: "auto" }}>
        <Table hover size="sm" className="mb-0 align-middle">
          <thead>
            <tr>
              <th>#</th>
              <th>Event</th>
              <th>Type</th>
              <th className="text-end">Views</th>
              <th className="text-end">Tickets</th>
              <th className="text-end">Orders</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((event, index) => (
              <tr key={event.id}>
                <td className="text-muted">{index + 1}</td>
                <td>
                  <div className="fw-semibold">{event.title}</div>
                  <div className="small text-muted">
                    {event.monthLabel} · {event.status || "—"} · {event.orgId || "No org"}
                  </div>
                </td>
                <td>{event.type}</td>
                <td className="text-end fw-semibold">{event.views}</td>
                <td className="text-end">{event.tickets}</td>
                <td className="text-end">{event.orders}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  };

  const renderOrderEventsTable = (rows, emptyText, emphasizeAmount = false) => {
    if (rows.length === 0) {
      return <div className="text-muted small">{emptyText}</div>;
    }

    return (
      <div className="table-responsive" style={{ maxHeight: 520, overflowY: "auto" }}>
        <Table hover size="sm" className="mb-0 align-middle">
          <thead>
            <tr>
              <th>Event</th>
              <th className="text-end">Orders</th>
              <th className="text-end">Paid</th>
              <th className="text-end">Free</th>
              <th className="text-end">Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((event) => (
              <tr key={event.id}>
                <td>
                  <div className="fw-semibold">{event.title}</div>
                  <div className="small text-muted">
                    {event.monthLabel} · {event.status || "—"} · {event.type || "—"}
                  </div>
                </td>
                <td className="text-end">{event.orders}</td>
                <td className="text-end">{event.paidOrders}</td>
                <td className="text-end">{event.freeOrders}</td>
                <td className={emphasizeAmount ? "text-end fw-semibold" : "text-end"}>
                  {event.orderTotal.toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  };

  // Monthly summary based on startDate: events, users, tickets, views, free/paid events and orders
  const monthlyEventSummary = (() => {
    if (!events.length) return [];
    const map = new Map();
    events.forEach((ev) => {
      const d = ev.startDate;
      if (!d) return;
      const date = d instanceof Date ? d : new Date(d);
      if (isNaN(date.getTime())) return;
      const year = date.getFullYear();
      const monthIndex = date.getMonth();
      const key = `${year}-${String(monthIndex + 1).padStart(2, "0")}`;
      const monthLabel = date.toLocaleString(undefined, { month: "short" });
      const label = `${monthLabel}-${String(year).slice(-2)}`; // e.g. Jul-24

      const users =
        (ev.summary && typeof ev.summary.users === "number" && ev.summary.users) ||
        (typeof ev.memberCount === "number" ? ev.memberCount : 0);
      const tickets =
        (ev.summary && typeof ev.summary.tickets === "number" && ev.summary.tickets) ||
        (typeof ev.ticketCount === "number" ? ev.ticketCount : 0);
      const views =
        (ev.summary && typeof ev.summary.views === "number" && ev.summary.views) ||
        (typeof ev.viewCount === "number" ? ev.viewCount : 0);
      const summary = ev.summary || {};
      const orders =
        (summary && typeof summary.orders === "number" ? summary.orders : 0) ||
        (typeof ev.orderCount === "number" ? ev.orderCount : 0);
      const orderTotalRaw =
        (summary && typeof summary.orderTotal === "number" ? summary.orderTotal : 0) ||
        (typeof ev.orderTotal === "number" ? ev.orderTotal : 0);
      const orderTotal = Math.round(orderTotalRaw * 100) / 100;

      // Per-event free/paid orders
      let freeOrders = 0;
      let paidOrders = 0;
      if (typeof summary.freeOrders === "number" || typeof summary.paidOrders === "number") {
        freeOrders = typeof summary.freeOrders === "number" ? summary.freeOrders : 0;
        paidOrders = typeof summary.paidOrders === "number" ? summary.paidOrders : 0;
      } else if (orders > 0) {
        if (orderTotal === 0) {
          freeOrders = orders;
        } else {
          paidOrders = orders;
        }
      }

      // Classify event as free / paid based on amount
      const isFreeEvent = freeOrders > 0 && paidOrders === 0;
      const isPaidEvent = paidOrders > 0 && freeOrders === 0;

      const prev =
        map.get(key) || {
          key,
          label,
          events: 0,
          users: 0,
          tickets: 0,
          views: 0,
          orders: 0,
          orderTotal: 0,
          freeEvents: 0,
          paidEvents: 0,
          freeOrders: 0,
          paidOrders: 0,
        };
      prev.events += 1;
      prev.users += users || 0;
      prev.tickets += tickets || 0;
      prev.views += views || 0;
      prev.orders += orders || 0;
      prev.orderTotal = Math.round((prev.orderTotal + (orderTotal || 0)) * 100) / 100;
      if (isFreeEvent) prev.freeEvents += 1;
      if (isPaidEvent) prev.paidEvents += 1;
      prev.freeOrders += freeOrders || 0;
      prev.paidOrders += paidOrders || 0;
      map.set(key, prev);
    });
    return Array.from(map.values()).sort((a, b) => a.key.localeCompare(b.key));
  })();

  // Month-to-month trend: delta vs previous month for users, tickets, views
  const monthlyTrend = (() => {
    if (!monthlyEventSummary.length) return [];
    return monthlyEventSummary.map((row, i) => {
      const prev = i > 0 ? monthlyEventSummary[i - 1] : null;
      const deltaUsers = prev ? row.users - prev.users : null;
      const deltaTickets = prev ? row.tickets - prev.tickets : null;
      const deltaViews = prev ? row.views - prev.views : null;
      return {
        ...row,
        deltaUsers,
        deltaTickets,
        deltaViews,
      };
    });
  })();

  const handleExportMonthlySummary = () => {
    if (!monthlyEventSummary.length) return;
    const header = [
      "Month",
      "Events",
      "Free events",
      "Paid events",
      "Users",
      "Tickets",
      "Views",
      "Orders",
      "Free orders",
      "Paid orders",
      "Order total",
    ];
    const rows = monthlyEventSummary.map((row) => [
      row.label,
      row.events ?? 0,
      row.freeEvents ?? 0,
      row.paidEvents ?? 0,
      row.users ?? 0,
      row.tickets ?? 0,
      row.views ?? 0,
      row.orders ?? 0,
      row.freeOrders ?? 0,
      row.paidOrders ?? 0,
      typeof row.orderTotal === "number"
        ? Number(row.orderTotal).toFixed(2)
        : "0.00",
    ]);
    const csvLines = [header, ...rows]
      .map((cols) =>
        cols
          .map((val) => {
            const s = String(val ?? "");
            if (s.includes(",") || s.includes('"') || s.includes("\n")) {
              return `"${s.replace(/"/g, '""')}"`;
            }
            return s;
          })
          .join(",")
      )
      .join("\n");
    const blob = new Blob([csvLines], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "monthly-event-summary.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleSetApproved = async (event, approvedValue) => {
    if (!event?.id) return;
    setUpdatingApprovalId(event.id);
    setError("");
    try {
      await fires.collection("events").doc(event.id).update({ approved: approvedValue });
      setEvents((prev) =>
        prev.map((e) =>
          e.id === event.id
            ? {
                ...e,
                approved: approvedValue,
              }
            : e
        )
      );
    } catch (err) {
      console.error("Failed to update approved flag:", err);
      setError(err?.message || "Failed to update approved flag for event");
    } finally {
      setUpdatingApprovalId(null);
    }
  };

  const visibleEvents = (() => {
    if (selectedMonthKey === "all") return events;
    return events.filter((ev) => {
      const d = ev.startDate;
      if (!d) return false;
      const date = d instanceof Date ? d : new Date(d);
      if (isNaN(date.getTime())) return false;
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
      return key === selectedMonthKey;
    });
  })();

  // Totals across all events (up to now)
  const totals = (() => {
    let users = 0;
    let views = 0;
    let tickets = 0;
    let orders = 0;
    let orderTotal = 0;
    let freeOrders = 0;
    let paidOrders = 0;
    events.forEach((ev) => {
      const summary = ev.summary || {};
      users +=
        (summary && typeof summary.users === "number" ? summary.users : 0) ||
        (typeof ev.memberCount === "number" ? ev.memberCount : 0);
      tickets +=
        (summary && typeof summary.tickets === "number" ? summary.tickets : 0) ||
        (typeof ev.ticketCount === "number" ? ev.ticketCount : 0);
      views +=
        (summary && typeof summary.views === "number" ? summary.views : 0) ||
        (typeof ev.viewCount === "number" ? ev.viewCount : 0);
      const derivedOrders =
        (summary && typeof summary.orders === "number" ? summary.orders : 0) ||
        (typeof ev.orderCount === "number" ? ev.orderCount : 0);
      const derivedOrderTotalRaw =
        (summary && typeof summary.orderTotal === "number" ? summary.orderTotal : 0) ||
        (typeof ev.orderTotal === "number" ? ev.orderTotal : 0);
      const derivedOrderTotal = Math.round(derivedOrderTotalRaw * 100) / 100;

      orders += derivedOrders;
      orderTotal += derivedOrderTotal;

      // Prefer explicit freeOrders / paidOrders from summary when available
      if (typeof summary.freeOrders === "number" || typeof summary.paidOrders === "number") {
        if (typeof summary.freeOrders === "number") freeOrders += summary.freeOrders;
        if (typeof summary.paidOrders === "number") paidOrders += summary.paidOrders;
      } else if (derivedOrders > 0) {
        // Fallback: if total amount is 0, treat all as free; otherwise treat all as paid
        if (derivedOrderTotal === 0) {
          freeOrders += derivedOrders;
        } else {
          paidOrders += derivedOrders;
        }
      }
    });
    return {
      events: events.length,
      users,
      tickets,
      views,
      orders,
      orderTotal: Math.round(orderTotal * 100) / 100,
      freeOrders,
      paidOrders,
    };
  })();

  if (checkingAdmin || !currentUser) {
    return (
      <Container className="py-5">
        <div className="d-flex justify-content-center align-items-center">
          <Spinner animation="border" />
          <span className="ms-2">Loading...</span>
        </div>
      </Container>
    );
  }

  if (!isAdmin) {
    return (
      <Container className="py-5">
        <Alert variant="danger">Access denied. Admin only.</Alert>
        <Button variant="outline-primary" onClick={() => navigate("/")}>
          Back
        </Button>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">Event Overview</h2>
          <p className="text-muted mb-0">
            All events from <code>events</code>, ordered by <code>startDate</code>.
          </p>
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <div className="d-flex align-items-center gap-1">
            <span className="small text-muted">Show:</span>
            <Form.Select
              size="sm"
              value={selectedMonthKey}
              onChange={(e) => setSelectedMonthKey(e.target.value)}
            >
              <option value="all">All months</option>
              {monthlyEventSummary.map((row) => (
                <option key={row.key} value={row.key}>
                  {row.label}
                </option>
              ))}
            </Form.Select>
          </div>
          {summarizingAll && summarizeProgress.total > 0 && (
            <span className="small text-muted">
              Summarizing {summarizeProgress.current}/{summarizeProgress.total} events (
              {Math.round(
                (summarizeProgress.current / summarizeProgress.total) * 100
              )}
              %)
            </span>
          )}
          <Button
            size="sm"
            variant="outline-secondary"
            disabled={summarizingAll || loading || visibleEvents.length === 0}
            onClick={async () => {
              try {
                setSummarizingAll(true);
                const total = visibleEvents.length;
                setSummarizeProgress({ current: 0, total });
                let processed = 0;
                for (const ev of visibleEvents) {
                  try {
                    setSummarizingId(ev.id);
                    const membersSnap = await fires
                      .collection("events")
                      .doc(ev.id)
                      .collection("members")
                      .get();
                    const userCount = membersSnap.size;

                    const ticketsSnap = await fires
                      .collection("events")
                      .doc(ev.id)
                      .collection("tickets")
                      .get();
                    const ticketCount = ticketsSnap.size;

                    const viewsSnap = await fires
                      .collection("events")
                      .doc(ev.id)
                      .collection("views")
                      .get();
                    const viewCount = viewsSnap.size;

                    //get orders related to this event
                    const ordersSnap = await fires
                      .collection("orders")
                      .where("eventId", "==", ev.id)
                      .get();
                    const orderCount = ordersSnap.size;
                    const orderTotal =
                      Math.round(
                        ordersSnap.docs.reduce((acc, doc) => acc + (doc.data().amount || 0), 0) * 100
                      ) / 100;

                      let freeOrderCount = 0;
                      let paidOrderCount = 0;

                      ordersSnap.docs.forEach((doc) => {  
                        const orderData = doc.data();
                        const orderAmount = orderData.amount || 0;
                        if (orderAmount === 0) {
                          freeOrderCount += 1;
                        } else {
                          paidOrderCount += 1;
                        }
                      });

                    await fires.collection("events").doc(ev.id).update({
                      summary: {
                        users: userCount,
                        tickets: ticketCount,
                        views: viewCount,
                        orders: orderCount,
                        freeOrders: freeOrderCount,
                        paidOrders: paidOrderCount,
                        orderTotal,
                      },
                    });

                    setEvents((prev) =>
                      prev.map((e) =>
                        e.id === ev.id
                          ? {
                              ...e,
                              summary: {
                                ...(e.summary || {}),
                                users: userCount,
                                tickets: ticketCount,
                                views: viewCount,
                                orders: orderCount,
                                paidOrders: paidOrderCount,
                                freeOrders: freeOrderCount,
                                orderTotal,
                              },
                              memberCount: userCount,
                              ticketCount,
                              viewCount,
                              orderCount,
                              orderTotal,
                            }
                          : e
                      )
                    );
                  } catch (err) {
                    console.error("Failed to summarize event:", ev.id, err);
                  } finally {
                    processed += 1;
                    setSummarizeProgress({ current: processed, total });
                    setSummarizingId(null);
                  }
                }
                window.alert("All events summarized.");
              } catch (err) {
                console.error("Failed to summarize all events:", err);
                window.alert("Failed to summarize all events. Please try again.");
              } finally {
                setSummarizingAll(false);
                setSummarizingId(null);
              }
            }}
          >
            {summarizingAll ? "Summarizing…" : "Summarize All"}
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      <Card className="shadow-sm mb-4">
        <Card.Body className="py-3 px-3">
          <h6 className="mb-3">Totals (up to now)</h6>
          <div className="d-flex flex-wrap gap-3">
            <div className="border rounded px-3 py-2">
              <div className="small text-muted">Total events</div>
              <div className="fw-bold fs-5">{totals.events}</div>
            </div>
            <div className="border rounded px-3 py-2">
              <div className="small text-muted">Total users</div>
              <div className="fw-bold fs-5">{totals.users}</div>
            </div>
            <div className="border rounded px-3 py-2">
              <div className="small text-muted">Total views</div>
              <div className="fw-bold fs-5">{totals.views}</div>
            </div>
            <div className="border rounded px-3 py-2">
              <div className="small text-muted">Total tickets</div>
              <div className="fw-bold fs-5">{totals.tickets}</div>
            </div>
            <div className="border rounded px-3 py-2">
              <div className="small text-muted">Total orders</div>
              <div className="fw-bold fs-5">{totals.orders}</div>
            </div>
            <div className="border rounded px-3 py-2">
              <div className="small text-muted">Total order amount</div>
              <div className="fw-bold fs-5">
                {typeof totals.orderTotal === "number" ? totals.orderTotal.toFixed(2) : "0.00"}
              </div>
            </div>
            <div className="border rounded px-3 py-2">
              <div className="small text-muted">Total free orders</div>
              <div className="fw-bold fs-5">{totals.freeOrders}</div>
            </div>
            <div className="border rounded px-3 py-2">
              <div className="small text-muted">Total paid orders</div>
              <div className="fw-bold fs-5">{totals.paidOrders}</div>
            </div>
          </div>
        </Card.Body>
      </Card>

      <Card className="shadow-sm mb-4">
        <Card.Body className="py-3 px-3">
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <div>
              <h6 className="mb-1">All-Time Customer Preferences</h6>
              <p className="text-muted small mb-0">
                Unique customers by <code>orders.userEmail</code>, joined with event data for preference tags.
              </p>
            </div>
            <div className="d-flex gap-2 flex-wrap">
              <Button
                size="sm"
                variant="outline-primary"
                onClick={loadCustomerPreferences}
                disabled={loadingCustomerPreferences || loading || events.length === 0}
              >
                {loadingCustomerPreferences ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Loading...
                  </>
                ) : (
                  "Load Customer Preferences"
                )}
              </Button>
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={handleExportCustomerPreferences}
                disabled={customerPreferenceRows.length === 0}
              >
                Export Customer CSV
              </Button>
            </div>
          </div>

          {customerPreferenceRows.length > 0 ? (
            <div className="table-responsive" style={{ maxHeight: 520, overflowY: "auto" }}>
              <Table hover size="sm" className="mb-0 align-middle">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th className="text-end">Orders</th>
                    <th className="text-end">Spent</th>
                    <th>Favorite types</th>
                    <th>Top events</th>
                    <th>Suggested tags</th>
                  </tr>
                </thead>
                <tbody>
                  {customerPreferenceRows.map((row) => (
                    <tr key={row.email}>
                      <td>
                        <div className="fw-semibold">{row.email}</div>
                        {row.names && <div className="small text-muted">{row.names}</div>}
                      </td>
                      <td className="text-end">
                        <div className="fw-semibold">{row.orderCount}</div>
                        <div className="small text-muted">
                          {row.paidOrderCount} paid / {row.freeOrderCount} free
                        </div>
                      </td>
                      <td className="text-end">{row.totalSpent.toFixed(2)}</td>
                      <td className="small">{formatCountTags(row.topTypes) || "—"}</td>
                      <td className="small text-break">{formatCountTags(row.topEvents) || "—"}</td>
                      <td className="small text-break">{row.suggestedTags || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <div className="text-muted small border rounded p-3">
              Load customer preferences to generate an all-time unique-email list from orders.
            </div>
          )}
        </Card.Body>
      </Card>

      <Card className="shadow-sm mb-4">
        <Card.Body className="py-3 px-3">
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <div>
              <h6 className="mb-1">Popular Events Report</h6>
              <p className="text-muted small mb-0">
                Ranked by summarized event views. Run Summarize All to refresh the source data.
              </p>
            </div>
            <div className="d-flex gap-2 flex-wrap">
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={handleExportPopularEvents}
                disabled={popularEventReport.byViews.length === 0}
              >
                Export Top 150 Views CSV
              </Button>
            </div>
          </div>

          <div className="border rounded p-3">
            <div className="fw-semibold mb-2">Top 150 most visited events</div>
            {popularEventReport.byViews.length > 0 ? (
              <div className="table-responsive" style={{ maxHeight: 520, overflowY: "auto" }}>
                <Table hover size="sm" className="mb-0 align-middle">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Event</th>
                      <th>Type</th>
                      <th className="text-end">Views</th>
                      <th className="text-end">Tickets</th>
                      <th className="text-end">Orders</th>
                    </tr>
                  </thead>
                  <tbody>
                    {popularEventReport.byViews.map((event, index) => (
                      <tr key={event.id}>
                        <td className="text-muted">{index + 1}</td>
                        <td>
                          <div className="fw-semibold">{event.title}</div>
                          <div className="small text-muted">
                            {event.monthLabel} · {event.status || "—"} · {event.orgId || "No org"}
                          </div>
                        </td>
                        <td>{event.type}</td>
                        <td className="text-end fw-semibold">{event.views}</td>
                        <td className="text-end">{event.tickets}</td>
                        <td className="text-end">{event.orders}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            ) : (
              <div className="text-muted small">No summarized event views found.</div>
            )}
          </div>
        </Card.Body>
      </Card>

      <Card className="shadow-sm mb-4">
        <Card.Body className="py-3 px-3">
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <div>
              <h6 className="mb-1">Order Event Report</h6>
              <p className="text-muted small mb-0">
                Ranked by summarized order counts and order amount.
              </p>
            </div>
            <div className="d-flex gap-2 flex-wrap">
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={handleExportMostOrderedEvents}
                disabled={orderReport.byOrders.length === 0}
              >
                Export Top 150 Orders CSV
              </Button>
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={handleExportOrderAmountEvents}
                disabled={orderReport.byOrderAmount.length === 0}
              >
                Export Top 150 Amount CSV
              </Button>
            </div>
          </div>
          <div className="row g-3">
            <div className="col-12 col-xl-6">
              <div className="border rounded p-3 h-100">
                <div className="fw-semibold mb-2">Top 150 most ordered events</div>
                {orderReport.byOrders.length > 0 ? (
                  <div className="table-responsive" style={{ maxHeight: 520, overflowY: "auto" }}>
                    <Table hover size="sm" className="mb-0 align-middle">
                      <thead>
                        <tr>
                          <th>Event</th>
                          <th className="text-end">Orders</th>
                          <th className="text-end">Paid</th>
                          <th className="text-end">Free</th>
                          <th className="text-end">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orderReport.byOrders.map((event) => (
                          <tr key={event.id}>
                            <td>
                              <div className="fw-semibold">{event.title}</div>
                              <div className="small text-muted">
                                {event.monthLabel} · {event.status || "—"} · {event.type || "—"}
                              </div>
                            </td>
                            <td className="text-end">{event.orders}</td>
                            <td className="text-end">{event.paidOrders}</td>
                            <td className="text-end">{event.freeOrders}</td>
                            <td className="text-end">{event.orderTotal.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-muted small">No summarized orders found.</div>
                )}
              </div>
            </div>

            <div className="col-12 col-xl-6">
              <div className="border rounded p-3 h-100">
                <div className="fw-semibold mb-2">Top 150 highest order amount events</div>
                {orderReport.byOrderAmount.length > 0 ? (
                  <div className="table-responsive" style={{ maxHeight: 520, overflowY: "auto" }}>
                    <Table hover size="sm" className="mb-0 align-middle">
                      <thead>
                        <tr>
                          <th>Event</th>
                          <th className="text-end">Orders</th>
                          <th className="text-end">Paid</th>
                          <th className="text-end">Free</th>
                          <th className="text-end">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orderReport.byOrderAmount.map((event) => (
                          <tr key={event.id}>
                            <td>
                              <div className="fw-semibold">{event.title}</div>
                              <div className="small text-muted">
                                {event.monthLabel} · {event.status || "—"} · {event.type || "—"}
                              </div>
                            </td>
                            <td className="text-end">{event.orders}</td>
                            <td className="text-end">{event.paidOrders}</td>
                            <td className="text-end">{event.freeOrders}</td>
                            <td className="text-end fw-semibold">{event.orderTotal.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-muted small">No summarized order amount found.</div>
                )}
              </div>
            </div>
          </div>

          <div className="border rounded p-3 mt-3">
            <div className="fw-semibold mb-2">Similarities</div>
            <ul className="small mb-0 ps-3">
              {orderReport.similarityNotes.map((note) => (
                <li key={note}>{note}</li>
              ))}
            </ul>
          </div>
        </Card.Body>
      </Card>

      <Card className="shadow-sm mb-4">
        <Card.Body className="py-3 px-3">
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <div>
              <h6 className="mb-1">{currentYear} Event Listing</h6>
              <p className="text-muted small mb-0">
                Current-year rankings based on events with a {currentYear} start date.
              </p>
            </div>
            <div className="d-flex gap-2 flex-wrap">
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={handleExportCurrentYearPopularEvents}
                disabled={currentYearPopularEventReport.byViews.length === 0}
              >
                Export {currentYear} Views CSV
              </Button>
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={handleExportCurrentYearMostOrderedEvents}
                disabled={currentYearOrderReport.byOrders.length === 0}
              >
                Export {currentYear} Orders CSV
              </Button>
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={handleExportCurrentYearOrderAmountEvents}
                disabled={currentYearOrderReport.byOrderAmount.length === 0}
              >
                Export {currentYear} Amount CSV
              </Button>
            </div>
          </div>

          <div className="row g-3">
            <div className="col-12 col-xl-4">
              <div className="border rounded p-3 h-100">
                <div className="fw-semibold mb-2">{currentYear} top 150 most viewed events</div>
                {renderViewedEventsTable(
                  currentYearPopularEventReport.byViews,
                  `No summarized ${currentYear} event views found.`
                )}
              </div>
            </div>
            <div className="col-12 col-xl-4">
              <div className="border rounded p-3 h-100">
                <div className="fw-semibold mb-2">{currentYear} top 150 most ordered events</div>
                {renderOrderEventsTable(
                  currentYearOrderReport.byOrders,
                  `No summarized ${currentYear} orders found.`
                )}
              </div>
            </div>
            <div className="col-12 col-xl-4">
              <div className="border rounded p-3 h-100">
                <div className="fw-semibold mb-2">{currentYear} top 150 highest order amount events</div>
                {renderOrderEventsTable(
                  currentYearOrderReport.byOrderAmount,
                  `No summarized ${currentYear} order amount found.`,
                  true
                )}
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>

      {monthlyEventSummary.length > 0 && (
        <Card className="shadow-sm mb-4">
          <Card.Body className="py-3 px-3">
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <h6 className="mb-0">Monthly Event Summary (by start date)</h6>
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={handleExportMonthlySummary}
              >
                Export CSV
              </Button>
            </div>
            <div
              className="table-responsive"
              style={{ maxHeight: 400, overflowY: "auto" }}
            >
              <Table hover size="sm" className="mb-0">
                <thead
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    backgroundColor: "#ffffff",
                  }}
                >
                  <tr>
                    <th>Month</th>
                    <th className="text-end">Events</th>
                  <th className="text-end">Free events</th>
                  <th className="text-end">Paid events</th>
                    <th className="text-end">Users</th>
                    <th className="text-end">Tickets</th>
                    <th className="text-end">Views</th>
                    <th className="text-end">Orders</th>
                    <th className="text-end">Free orders</th>
                    <th className="text-end">Paid orders</th>
                    <th className="text-end">Order Total</th>
                  </tr>
                </thead>
                <tbody>
                  {monthlyEventSummary.map((row) => (
                    <tr key={row.key}>
                      <td>{row.label}</td>
                      <td className="text-end">{row.events}</td>
                    <td className="text-end">{row.freeEvents}</td>
                    <td className="text-end">{row.paidEvents}</td>
                      <td className="text-end">{row.users}</td>
                      <td className="text-end">{row.tickets}</td>
                      <td className="text-end">{row.views}</td>
                      <td className="text-end">{row.orders}</td>
                      <td className="text-end">{row.freeOrders}</td>
                      <td className="text-end">{row.paidOrders}</td>
                      <td className="text-end">
                        {typeof row.orderTotal === "number"
                          ? Number(row.orderTotal).toFixed(2)
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      )}

      {monthlyEventSummary.length > 0 && (
        <>
          <Card className="shadow-sm mb-3">
            <Card.Body className="py-3 px-3">
              <h6 className="mb-3">Users per Month</h6>
              <div style={{ width: "100%", height: 260 }}>
                <ResponsiveContainer>
                  <LineChart
                    data={monthlyEventSummary}
                    margin={{ top: 8, right: 16, left: 40, bottom: 8 }}
                    layout="vertical"
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis
                      type="category"
                      dataKey="label"
                      tick={{ fontSize: 11 }}
                      width={70}
                    />
                    <Tooltip labelFormatter={(label) => `Month: ${label}`} />
                    <Line
                      type="monotone"
                      dataKey="users"
                      name="Users"
                      stroke="#0d6efd"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card.Body>
          </Card>

          <Card className="shadow-sm mb-3">
            <Card.Body className="py-3 px-3">
              <h6 className="mb-3">Tickets per Month</h6>
              <div style={{ width: "100%", height: 260 }}>
                <ResponsiveContainer>
                  <LineChart
                    data={monthlyEventSummary}
                    margin={{ top: 8, right: 16, left: 40, bottom: 8 }}
                    layout="vertical"
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis
                      type="category"
                      dataKey="label"
                      tick={{ fontSize: 11 }}
                      width={70}
                    />
                    <Tooltip labelFormatter={(label) => `Month: ${label}`} />
                    <Line
                      type="monotone"
                      dataKey="tickets"
                      name="Tickets"
                      stroke="#198754"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card.Body>
          </Card>

          <Card className="shadow-sm mb-4">
            <Card.Body className="py-3 px-3">
              <h6 className="mb-3">Views per Month</h6>
              <div style={{ width: "100%", height: 260 }}>
                <ResponsiveContainer>
                  <LineChart
                    data={monthlyEventSummary}
                    margin={{ top: 8, right: 16, left: 40, bottom: 8 }}
                    layout="vertical"
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis
                      type="category"
                      dataKey="label"
                      tick={{ fontSize: 11 }}
                      width={70}
                    />
                    <Tooltip labelFormatter={(label) => `Month: ${label}`} />
                    <Line
                      type="monotone"
                      dataKey="views"
                      name="Views"
                      stroke="#fd7e14"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card.Body>
          </Card>
        </>
      )}

      {monthlyTrend.length > 0 && (
        <Card className="shadow-sm mb-4">
          <Card.Body className="py-3 px-3">
            <h6 className="mb-3">Month-to-Month Trend (Users, Views, Tickets)</h6>
            <p className="text-muted small mb-2">
              Change vs previous month. Green = increase, red = decrease.
            </p>
            <div className="table-responsive">
              <Table hover size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th className="text-end">Users</th>
                    <th className="text-end">Δ Users</th>
                    <th className="text-end">Tickets</th>
                    <th className="text-end">Δ Tickets</th>
                    <th className="text-end">Views</th>
                    <th className="text-end">Δ Views</th>
                  </tr>
                </thead>
                <tbody>
                  {monthlyTrend.map((row) => (
                    <tr key={row.key}>
                      <td>{row.label}</td>
                      <td className="text-end">{row.users}</td>
                      <td className="text-end">
                        {row.deltaUsers == null ? (
                          "—"
                        ) : (
                          <span
                            className={
                              row.deltaUsers > 0
                                ? "text-success"
                                : row.deltaUsers < 0
                                ? "text-danger"
                                : "text-muted"
                            }
                          >
                            {row.deltaUsers > 0 ? "+" : ""}
                            {row.deltaUsers}
                          </span>
                        )}
                      </td>
                      <td className="text-end">{row.tickets}</td>
                      <td className="text-end">
                        {row.deltaTickets == null ? (
                          "—"
                        ) : (
                          <span
                            className={
                              row.deltaTickets > 0
                                ? "text-success"
                                : row.deltaTickets < 0
                                ? "text-danger"
                                : "text-muted"
                            }
                          >
                            {row.deltaTickets > 0 ? "+" : ""}
                            {row.deltaTickets}
                          </span>
                        )}
                      </td>
                      <td className="text-end">{row.views}</td>
                      <td className="text-end">
                        {row.deltaViews == null ? (
                          "—"
                        ) : (
                          <span
                            className={
                              row.deltaViews > 0
                                ? "text-success"
                                : row.deltaViews < 0
                                ? "text-danger"
                                : "text-muted"
                            }
                          >
                            {row.deltaViews > 0 ? "+" : ""}
                            {row.deltaViews}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      )}

      <Card className="shadow-sm">
        <Card.Body className="p-0">
          {loading ? (
            <div className="py-4 text-center">
              <Spinner animation="border" size="sm" />
              <span className="ms-2">Loading events...</span>
            </div>
          ) : visibleEvents.length === 0 ? (
            <div className="py-4 text-center text-muted">No events found.</div>
          ) : (
            <div className="table-responsive">
              <Table hover size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Event</th>
                    <th>Event ID</th>
                    <th>Org ID</th>
                    <th>Start</th>
                    <th>End</th>
                    <th>Status</th>
                    <th>Type</th>
                    <th className="text-end">Views</th>
                    <th className="text-end">Users</th>
                    <th className="text-end">Tickets</th>
                    <th className="text-end">Orders</th>
                    <th className="text-end">Order Total</th>
                    <th>Approved</th>
                    <th className="text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleEvents.map((event, index) => (
                    <tr key={event.id}>
                      <td className="text-muted">{index + 1}</td>
                      <td>{event.title}</td>
                      <td>
                        <span className="font-monospace small">{event.id}</span>
                      </td>
                      <td>
                        <span className="font-monospace small">{event.orgId || "—"}</span>
                      </td>
                      <td>{formatDateTime(event.startDate)}</td>
                      <td>{formatDateTime(event.endDate)}</td>
                      <td>
                        <Badge bg={event.status === "live" ? "success" : "secondary"}>
                          {event.status}
                        </Badge>
                      </td>
                      <td>{event.type || "—"}</td>
                      <td className="text-end">
                        {event.summary && typeof event.summary.views === "number"
                          ? event.summary.views
                          : typeof event.viewCount === "number"
                          ? event.viewCount
                          : "—"}
                      </td>
                      <td className="text-end">
                        {event.summary && typeof event.summary.users === "number"
                          ? event.summary.users
                          : typeof event.memberCount === "number"
                          ? event.memberCount
                          : "—"}
                      </td>
                      <td className="text-end">
                        {event.summary && typeof event.summary.tickets === "number"
                          ? event.summary.tickets
                          : typeof event.ticketCount === "number"
                          ? event.ticketCount
                          : "—"}
                      </td>
                      <td className="text-end">
                        {event.summary && typeof event.summary.orders === "number"
                          ? event.summary.orders
                          : typeof event.orderCount === "number"
                          ? event.orderCount
                          : "—"}
                      </td>
                      <td className="text-end">
                        {event.summary && typeof event.summary.orderTotal === "number"
                          ? Number(event.summary.orderTotal).toFixed(2)
                          : typeof event.orderTotal === "number"
                          ? Number(event.orderTotal).toFixed(2)
                          : "—"}
                      </td>
                      <td>
                        <div className="d-flex flex-column align-items-start gap-1">
                          <div>
                            {event.approved === true ? (
                              <Badge bg="success">approved</Badge>
                            ) : event.approved === false ? (
                              <Badge bg="warning" text="dark">
                                pending
                              </Badge>
                            ) : (
                              <span className="text-muted small">n/a</span>
                            )}
                          </div>
                          <div className="d-flex gap-1">
                            <Button
                              size="sm"
                              variant={event.approved === true ? "success" : "outline-success"}
                              disabled={updatingApprovalId === event.id}
                              onClick={() => handleSetApproved(event, true)}
                            >
                              Yes
                            </Button>
                            <Button
                              size="sm"
                              variant={event.approved === false ? "warning" : "outline-warning"}
                              disabled={updatingApprovalId === event.id}
                              onClick={() => handleSetApproved(event, false)}
                            >
                              No
                            </Button>
                          </div>
                        </div>
                      </td>
                      <td className="text-end">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          disabled={summarizingId === event.id}
                          onClick={async () => {
                            try {
                              setSummarizingId(event.id);
                              const membersSnap = await fires
                                .collection("events")
                                .doc(event.id)
                                .collection("members")
                                .get();
                              const userCount = membersSnap.size;

                              const ticketsSnap = await fires
                                .collection("events")
                                .doc(event.id)
                                .collection("tickets")
                                .get();
                              const ticketCount = ticketsSnap.size;

                              const viewsSnap = await fires
                                .collection("events")
                                .doc(event.id)
                                .collection("views")
                                .get();
                              const viewCount = viewsSnap.size;
                              const ordersSnap = await fires
                                .collection("orders")
                                .where("eventId", "==", event.id)
                                .get();
                              const orderCount = ordersSnap.size;
                              const orderTotal =
                                Math.round(
                                  ordersSnap.docs.reduce((acc, doc) => acc + (doc.data().amount || 0), 0) * 100
                                ) / 100;
                                let freeOrderCount = 0;
                                let paidOrderCount = 0;
                                ordersSnap.docs.forEach((doc) => {
                                  const orderAmount = doc.data().amount || 0;
                                  if (orderAmount === 0) {
                                    freeOrderCount += 1;
                                  } else {
                                    paidOrderCount += 1;
                                  }
                                });

                              await fires.collection("events").doc(event.id).update({
                                summary: {
                                  users: userCount,
                                  tickets: ticketCount,
                                  views: viewCount,
                                  orders: orderCount,
                                  freeOrders: freeOrderCount,
                                  paidOrders: paidOrderCount,
                                  orderTotal,
                                },
                              });
                              setSummarizingId(null);
                              // Update local state so table reflects new summary immediately
                              setEvents((prev) =>
                                prev.map((e) =>
                                  e.id === event.id
                                    ? {
                                        ...e,
                                        summary: {
                                          ...(e.summary || {}),
                                          users: userCount,
                                          tickets: ticketCount,
                                          views: viewCount,
                                          orders: orderCount,
                                          freeOrders: freeOrderCount,
                                          paidOrders: paidOrderCount,
                                          orderTotal,
                                        },
                                        memberCount: userCount,
                                        ticketCount,
                                        viewCount,
                                        orderCount,
                                        orderTotal,
                                      }
                                    : e
                                )
                              );
                              window.alert("Event summarized successfully");
                            } catch (err) {
                              console.error("Failed to summarize event:", err);
                              window.alert("Failed to summarize event. Please try again.");
                            } finally {
                              setSummarizingId(null);
                            }
                          }}
                        >
                          {summarizingId === event.id ? "Summarizing..." : "Summarize"}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}
